import React, { useEffect, useState } from 'react'
import TagManager from 'react-gtm-module'

import { Drawer, Icon, ModalBox, useMediaQueryContext } from '@fourthwall/components'
import { CloseIcon } from '@fourthwall/icons'

import type { LaunchStrategyData } from '../../LaunchStrategy.types'
import * as S from './LaunchStrategyModal.styled'
import { ContentHeader } from './components/ContentHeader/ContentHeader'
import { Guide } from './components/Guide/Guide'
import { Navigation } from './components/Navigation/Navigation'
import { useLaunchStrategyModal } from './hooks/useLaunchStrategyModal'

export type LaunchStrategyModalProps = {
  data: LaunchStrategyData['modal']
  initialCategoryIndex: number
  initialGuideIndex: number
  totalGuides: number
  onClose: (absoluteActiveGuideIndex: number) => void
  onProgressChange: (categoryIndex: number, guideIndex: number) => void
}

export const LaunchStrategyModal = ({
  data,
  initialCategoryIndex,
  initialGuideIndex,
  totalGuides,
  onClose,
  onProgressChange,
}: LaunchStrategyModalProps) => {
  const [headGuideLoaded, setHeadGuideLoaded] = useState(false)
  const media = useMediaQueryContext()
  const {
    activeCategoryIndex,
    activeGuideIndex,
    absoluteActiveGuideIndex,
    drawer,
    guide,
    onNext,
    onPrevious,
    onChange,
  } = useLaunchStrategyModal({
    categories: data.categories,
    initialCategoryIndex,
    initialGuideIndex,
    onProgressChange,
  })
  const allGuidesUrls = data.categories.flatMap((category) =>
    category.guides.map((guide) => guide.url),
  )

  useEffect(() => {
    TagManager.dataLayer({
      dataLayer: { event: 'launch_strategy_modal' },
    })
  }, [])

  const handleClose = () => onClose(absoluteActiveGuideIndex)

  const renderDrawer = () => {
    if (media.tablet) return

    return (
      <Drawer>
        {drawer.drawerActive && (
          <Drawer.Body
            position="left"
            width="80%"
            overlay={<Drawer.Overlay onClick={drawer.drawerClose} />}
          >
            <Drawer.Content>
              <S.Sidebar>
                <Navigation
                  categories={data.categories}
                  activeGuideIndex={activeGuideIndex}
                  activeCategoryIndex={activeCategoryIndex}
                  onChange={onChange}
                  onGuideSelect={drawer.drawerClose}
                />
              </S.Sidebar>
            </Drawer.Content>
          </Drawer.Body>
        )}
      </Drawer>
    )
  }

  return (
    <ModalBox
      fullWidth
      size={media.tablet ? undefined : 'fullScreen'}
      withoutPadding
      withoutCloseButton
      maxWidth={1060}
      onClose={handleClose}
    >
      <S.Container>
        {media.tablet && (
          <S.Sidebar>
            <S.TextualContent>
              <S.Title>{data.title}</S.Title>
              <S.Description>{data.description}</S.Description>
            </S.TextualContent>
            <Navigation
              categories={data.categories}
              activeGuideIndex={activeGuideIndex}
              activeCategoryIndex={activeCategoryIndex}
              onChange={onChange}
            />
          </S.Sidebar>
        )}
        <S.Content>
          {!media.tablet && (
            <S.ModalHeader>
              <S.TextualContent>
                <S.Title>{data.title}</S.Title>
                <S.Description>{data.description}</S.Description>
              </S.TextualContent>
              <S.CloseButton onClick={handleClose}>
                <Icon component={CloseIcon} height={16} />
              </S.CloseButton>
            </S.ModalHeader>
          )}
          <ContentHeader
            title={guide.title}
            progressValue={absoluteActiveGuideIndex + 1}
            maxProgressValue={totalGuides}
            onOpenDrawer={drawer.drawerOpen}
            onPrevious={onPrevious}
            onNext={onNext}
            onClose={handleClose}
          />
          <S.GuidesWrapper>
            {allGuidesUrls.map((url, index) => {
              const active = url === guide.url
              const shouldLoad =
                active ||
                headGuideLoaded ||
                index === absoluteActiveGuideIndex - 1 ||
                index === absoluteActiveGuideIndex + 1

              return (
                <Guide
                  key={index}
                  active={active}
                  shouldLoad={shouldLoad}
                  url={url}
                  onLoad={active ? () => setHeadGuideLoaded(true) : undefined}
                />
              )
            })}
          </S.GuidesWrapper>
        </S.Content>
      </S.Container>
      {renderDrawer()}
    </ModalBox>
  )
}
